import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { Card, Col } from 'antd'
import { CompanyTiny, KudosByReplyerFilterInput, EmployeeSearch, CompanyScope } from '../../../types'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { EmployeeSearchSelect } from './EmployeeSearchSelect'
import { CompanySearchSelect } from './CompanySearchSelect'
import { CreateKudosModalWithEmployeeSelect } from '../../../components/custom_components/CreateKudosModal/CreateKudosModalWithEmployeeSelect'
import { useCompanySearch } from '../../../graphql/company/hooks/useCompanySearch'
import { useActiveEmployeeSearch } from '../../employees/EmployeeSearch/useActiveEmployeeSearch'
import { KUDOS_DASHBOARD_PAGE_SIZE } from '../../../graphql/kudos/util'

interface KudosReplyerInfoBoxProps {
  filters: KudosByReplyerFilterInput
  onFiltersChange: (filters: KudosByReplyerFilterInput) => void
}

export const KudosReplyerInfoBox: FC<KudosReplyerInfoBoxProps> = ({ filters, onFiltersChange }) => {
  const { t } = useTranslation()

  const employeeSearch = useActiveEmployeeSearch(true)
  const companySearch = useCompanySearch(CompanyScope.All)

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearch | null>(null)
  const [selectedEmployeeCompany, setSelectedEmployeeCompany] = useState<CompanyTiny | null>(null)
  const [isCreateKudosModalVisible, setIsCreateKudosModalVisible] = useState(false)

  // Employee option selection handler
  const handleEmployeeOptionClick = useCallback(
    (employee: EmployeeSearch) => {
      setSelectedEmployee(employee)
      onFiltersChange({
        ...filters,
        employeeId: employee.id,
      })
      employeeSearch.handleClear()
    },
    [filters, onFiltersChange, employeeSearch],
  )

  // Handle clearing employee selection
  const handleEmployeeClear = useCallback(() => {
    setSelectedEmployee(null)
    onFiltersChange({
      ...filters,
      employeeId: undefined,
    })
    employeeSearch.handleClear()
  }, [filters, onFiltersChange, employeeSearch])

  // Handle employee company selection
  const handleEmployeeCompanyOptionClick = useCallback(
    (company: CompanyTiny) => {
      setSelectedEmployeeCompany(company)
      onFiltersChange({
        ...filters,
        employeeCompanyId: company.id,
      })
      companySearch.handleClear()
    },
    [filters, onFiltersChange, companySearch],
  )

  // Handle clearing company selection
  const handleCompanyClear = useCallback(() => {
    setSelectedEmployeeCompany(null)
    onFiltersChange({
      ...filters,
      employeeCompanyId: undefined,
    })
    companySearch.handleClear()
  }, [filters, onFiltersChange, companySearch])

  const handleClearFilters = () => {
    setSelectedEmployee(null)
    setSelectedEmployeeCompany(null)
    employeeSearch.handleClear()
    companySearch.handleClear()
    onFiltersChange({
      employeeId: undefined,
      employeeCompanyId: undefined,
      pagination: {
        offset: 0,
        limit: KUDOS_DASHBOARD_PAGE_SIZE,
      },
    })
  }

  const hasActiveFilters = () => {
    return !!(filters.employeeId || filters.employeeCompanyId)
  }

  return (
    <Card
      bordered={false}
      style={{
        backgroundColor: 'white',
        boxShadow: 'none',
        borderRadius: '8px',
        height: '160px',
      }}
    >
      <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
        {/* Header with title and add buttons */}
        <PerFlexRow justify="space-between" align="middle" width="fullWidth">
          <PerFlexRow align="middle">
            <PerText appearance="tile-heading">{t('kudos.management.title')}</PerText>
          </PerFlexRow>
          <PerButtonCircle
            icon="add_stroke"
            color="primary"
            variant="pastel"
            onClick={() => setIsCreateKudosModalVisible(true)}
            tooltip={t('kudos.management.createNewWithEmployee')}
            data-e2e="create-kudos-with-employee-button"
          />
        </PerFlexRow>

        <PerText mb="xxs" fontSize="s">
          {t('kudos.management.dashboardReplyerDisclaimer')}
        </PerText>

        {/* Filters - Simplified Layout */}
        <PerFlexRow width="fullWidth">
          {/* Employee Name */}
          <Col span={8}>
            <PerFlexRow direction="column">
              <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                {t('kudos.management.filters.employeeName')}
              </PerText>
              <EmployeeSearchSelect
                searchTerm={employeeSearch.searchTerm}
                onSearch={employeeSearch.handleSearch}
                onClear={handleEmployeeClear}
                employees={employeeSearch.employees}
                loading={employeeSearch.loading}
                shouldShowOptions={employeeSearch.shouldShowOptions}
                onOptionClick={handleEmployeeOptionClick}
                placeholder={t('kudos.management.filters.employeeNamePlaceholder')}
                selectedEmployee={selectedEmployee}
                size={'small'}
              />
            </PerFlexRow>
          </Col>

          {/* Employee Company */}
          <Col span={8}>
            <PerFlexRow direction="column">
              <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                {t('kudos.management.filters.employeeCompany')}
              </PerText>
              <CompanySearchSelect
                searchTerm={companySearch.searchTerm}
                onSearch={companySearch.handleSearch}
                onClear={handleCompanyClear}
                companies={companySearch.companies}
                loading={companySearch.loading}
                shouldShowOptions={companySearch.shouldShowOptions}
                onOptionClick={handleEmployeeCompanyOptionClick}
                placeholder={t('kudos.management.filters.employeeCompanyPlaceholder')}
                selectedCompany={selectedEmployeeCompany}
              />
            </PerFlexRow>
          </Col>

          {/* Clear Filters Button */}
          <Col span={8}>
            <PerFlexRow justify="end" align="bottom" className="fullHeight">
              <PerButtonCircle
                color="error"
                icon="delete_fill"
                variant="pastel"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters()}
                tooltip={t('kudos.management.filters.clear')}
              />
            </PerFlexRow>
          </Col>
        </PerFlexRow>
      </PerFlexRow>

      {/* Create Kudos Modal with Employee Select */}
      <CreateKudosModalWithEmployeeSelect
        visible={isCreateKudosModalVisible}
        closeModal={() => setIsCreateKudosModalVisible(false)}
        onSuccess={() => {
          setIsCreateKudosModalVisible(false)
        }}
        filters={{ ...filters, pagination: { offset: 0, limit: KUDOS_DASHBOARD_PAGE_SIZE } }}
        replyerView={true}
      />
    </Card>
  )
}

export default KudosReplyerInfoBox
